import { Button, Flex, message, Modal, Table, Tabs, Tooltip, Typography, Form, Input, DatePicker, Select, Row, Col } from "antd";
import { UsimInventoryItem } from "../../../../api/usim/inventory/inventoryItem";
import { useState } from "react";
import { title } from "process";
import { usePutInventoryItemMutation, usePutInventoryItemStatusMutation } from "../../../../api/usim/inventory/usimInventoryApi";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useGetProductPlansQuery } from "../../../../api/usim/uploads/products/productPlansApi";
import React from "react";

export default function ItemDetailModal(props: {
  item: UsimInventoryItem | null;
  onClose: () => void;
}) {
  const [msgApi, ctx] = message.useMessage();
  const [putItem] = usePutInventoryItemMutation();
  const [putRedemption] = usePutInventoryItemStatusMutation();
  const [isRaw, setIsRaw] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const dataTbl = props.item ? Object.entries(props.item).map(([key, value]) => ({
    key,
    value: typeof value === "object" ? JSON.stringify(value, null, 2) : String(value),
  })).toSorted((a, b) => a.key.localeCompare(b.key)) : [];
  const cols = [
    {
      title: "Field",
      dataIndex: "key",
      key: "key",
      width: 200,
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (text: string) => (
        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", margin: 0 }}>
          {text}
        </pre>
      ),
    },
  ];

  async function handleReturnInv() {
    if (!props.item) {
      return;
    }
    
   const data = {
      ...props.item,
      status: "REDEEMED"
    };
    
    await putRedemption(data).unwrap();
    msgApi.success("Item marked as redeemed successfully.");
    props.onClose();
  }

  const { data: productPlans, error: productPlansError, isFetching: productPlansFetching } = useGetProductPlansQuery();


  // Transform product types for dropdown options
  const productPlansOptions = React.useMemo(() => {
    if (!productPlans) return [];
    //do not show item if status is INACTIVE
    return productPlans.filter(item => item.status !== "INACTIVE").map(item => ({
      label: item.planName,
      value: item.planName,
    }));
  }, [productPlans]);
   
    // Handle product plan selection to auto-fill product type
    const handleProductPlanChange = (planName: string) => {
      const selectedPlan = productPlans?.find(plan => plan.planName === planName);
      if (selectedPlan) {
        form.setFieldsValue({
          productType: selectedPlan.type
        });
      }
    };
  const handleEdit = () => {
    setIsEditing(true);
    if (props.item) {
      form.setFieldsValue({
        iccid: props.item.ICCID,
        mobileNumber: props.item.mobileNumber,
        productType: props.item.productType,
        planName: props.item.planName,
        expiryDate: props.item.expiryDate ? dayjs(props.item.expiryDate) : null,
        activationDate: props.item.activationDate ? dayjs(props.item.activationDate) : null,
        activatedDate: props.item.activatedDate ? dayjs(props.item.activatedDate) : null,
        ordernumber: Array.isArray(props.item.ordernumber) ? props.item.ordernumber.join(", ") : props.item.ordernumber,
        inwardTime: props.item.inwardTime ? dayjs(props.item.inwardTime) : null,
      });
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!props.item) return;

      const updatedItem: UsimInventoryItem = {
        ...props.item,
        ICCID: values.iccid ? values.iccid.toString() : "",
        mobileNumber: values.mobileNumber ? values.mobileNumber : "",
        productType: values.productType ? values.productType : "",
        planName: values.planName ? values.planName : "",
        expiryDate: values.expiryDate ? dayjs(values.expiryDate).format("YYYY-MM-DD") : "",
        activationDate: values.activationDate ? dayjs(values.activationDate).format("YYYY-MM-DD HH:mm:ss") : "",
        activatedDate: values.activatedDate ? dayjs(values.activatedDate).format("YYYY-MM-DD HH:mm:ss") : "",
        ordernumber: values.ordernumber ? values.ordernumber : "",
        inwardTime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      };

      console.log("updatedItem", updatedItem);
      await putItem(updatedItem).unwrap();
      msgApi.success("Item updated successfully.");
      // setIsEditing(false);
      // props.onClose();
    } catch (error) {
      console.error("Error updating item:", error);
      msgApi.error("Failed to update item.");
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    form.resetFields();
  };

  // console.log("props.item", props.item);

  // console.log("dataTbl", dataTbl);
  return (
    <Modal
      title={props.item?.ICCID}
      open={props.item != null}
      onOk={isEditing ? handleSave : props.onClose}
      onCancel={props.onClose}
      okText={isEditing ? "Save" : "Close"}
      cancelText={isEditing ? "Cancel" : undefined}
      cancelButtonProps={isEditing ? {} : { style: { display: "none" } }}
      width={1000}
    >
      {ctx}
      <Tabs
        onChange={(key)=>{
          // console.log("onChange",key);
          if(key === "edit"){
          handleEdit();
          }
          else{
            setIsEditing(false);
          }
        }}
        type="card"
        items={[
          {
            label: "Database",
            key: "database",
            children: (
              <>
                <Flex justify="space-between" align="center">
                  <Typography.Text>Database Details</Typography.Text>
                  <Flex gap={8}>
                    <Button onClick={() => setIsRaw(!isRaw)}>
                      {isRaw ? "View Table" : "View Raw"}
                    </Button>
                  </Flex>
                </Flex>
                { isRaw &&
                  <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                    {JSON.stringify(props.item, null, 2)}
                  </pre>
                }
                { !isRaw &&
                  <Table
                    columns={cols}
                    dataSource={dataTbl}
                    size="small"
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: false,
                    }}
                  />
                }
              </>
            ),
          },
          {
            label: "Edit Details",
            key: "edit",
          
            children: (
              <Form
                form={form}
                layout="vertical"
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="ICCID"
                      name="iccid"
                      rules={[{ required: true, message: "ICCID is required" }]}
                    >
                      <Input placeholder="Enter ICCID" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Mobile Number"
                      name="mobileNumber"
                    >
                      <Input placeholder="Enter mobile number" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                  <Form.Item
              label="Product Plan"
              name="planName"
              rules={[{ required: true, message: "Product plan is required" }]}
            >
              <Select
                options={productPlansOptions}
                placeholder="Select product plan"
                onChange={handleProductPlanChange}
              />

                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Product Type"
                      name="productType"
                      rules={[{ required: true, message: "Product type is required" }]}
                    >
                      <Input placeholder="Enter product type" disabled />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Expiry Date"
                      name="expiryDate"
                      rules={[{ required: true, message: "Choose valid expiry date" }]}
                    >
                      <DatePicker placeholder="Select expiry date" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Activation Date"
                      name="activationDate"
                    >
                      <DatePicker placeholder="Select activation date" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Activated Date"
                      name="activatedDate"
                    >
                      <DatePicker placeholder="Select activated date" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Order Number"
                      name="ordernumber"
                    >
                      <Input placeholder="Enter order number" />
                    </Form.Item>
                  </Col>
                </Row>

                {/* <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      label="Inward Time"
                      name="inwardTime"
                    >
                      <DatePicker 
                        placeholder="Select inward time" 
                        style={{ width: "100%" }} 
                        showTime={{ format: 'HH:mm' }}
                        format="YYYY-MM-DD HH:mm"
                      />
                    </Form.Item>
                  </Col>
                </Row> */}
              </Form>
            ),
          },
          {
            label: "Actions",
            key: "actions",
            children: (
              <Flex gap={8} vertical>
                <Flex justify="space-between" align="center">
                  <Typography.Text>Mark as Unused</Typography.Text>
                  { props.item?.productType &&
                    <Button type="primary" onClick={handleReturnInv}>
                      Mark as Used
                    </Button>
                  }
                  { !props.item?.productType &&
                    <Tooltip title="This item is already marked as unused and may be reassigned.">
                      <Button type="primary" disabled>
                        Unavailable
                      </Button>
                    </Tooltip>
                  }
                </Flex>
              </Flex>
            ),
          },
        ]}
      />
    </Modal>
  );
}